import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { GlobalConstants } from '@globalConstants';
import { ICourse } from '@interfaces/ICourse';
import { IFormErrors } from '@interfaces/IFormsErrors';
import { IGroup } from '@interfaces/IGroup';
import { IAppState } from '@myStore/app.state';
import { GetActiveCoursesRequest } from '@myStore/courses-view/courses-view.actions';
import {
  selectCoursesList,
  selectLoadingCoursesList,
} from '@myStore/courses-view/courses-view.selector';
import {
  ClearCreateEventErrors,
  CreateEventRequest,
} from '@myStore/events-changes/events-changes.actions';
import {
  selectChangesEventFailure,
  selectChangesEventLoading,
  selectChangesEventSuccess,
} from '@myStore/events-changes/events-changes.selector';
import {
  ClearEvents,
  ClearGetEventsByPeriod,
} from '@myStore/events-view/events-view.actions';
import {
  ClearCourseGroups,
  GetActiveCourseGroupsRequest,
} from '@myStore/groups-view/groups-view.actions';
import { selectCourseGroups } from '@myStore/groups-view/groups-view.selector';
import { select, Store } from '@ngrx/store';
import { CustomDateAdapter } from '@shared/adapter/date.adapter';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { formatDatePicker } from '@shared/utils/dateUtils';
import { isControlsIsValid } from '@shared/utils/formUtils';
import { MyErrorStateMatcher } from '@validators/matcher';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-event-form',
  templateUrl: './event-form.component.html',
  styleUrls: ['./event-form.component.sass'],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: GlobalConstants.DEFAULT_DATEPICKER_FORMAT,
    },
  ],
})
export class EventFormComponent implements OnInit, OnDestroy {
  readonly types = GlobalConstants.EventTypes;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public sendLoading$ = this._store.pipe(select(selectChangesEventLoading));
  public courseLoading$ = this._store.pipe(select(selectLoadingCoursesList));
  public errors$: { [key: string]: string[] } | null = {};
  public courses$: ICourse[] | null = null;
  public groups$: IGroup[] | null = null;
  public eventForm = new UntypedFormGroup({
    course: new UntypedFormControl(''),
    group: new UntypedFormControl({ value: '', disabled: true }),
    type: new UntypedFormControl(Object.keys(this.types)[0], [
      Validators.required,
    ]),
    theme: new UntypedFormControl(''),
    date: new UntypedFormControl(GlobalConstants.dateFormat, [
      Validators.required,
    ]),
    time: new UntypedFormControl('19:00', [Validators.required]),
    name: new UntypedFormControl(null),
  });
  public dialogData = this._dialogRef.componentInstance.data;
  public requiredErrorMessage = 'Поле является обязательным';
  public matcher = new MyErrorStateMatcher();
  public groupId!: number;
  public periodId!: number;

  constructor(
    private _store: Store<IAppState>,
    private _toast: ToastrService,
    private _dialogRef: MatDialogRef<FormDialogComponent>,
    private _router: Router
  ) {}

  ngOnInit(): void {
    if (this.dialogData.groupId) {
      this.groupId = this.dialogData.groupId;
    }

    if (this.dialogData.periodId) {
      this.periodId = this.dialogData.periodId;
    }

    this._store
      .pipe(select(selectChangesEventSuccess), takeUntil(this.destroy$))
      .subscribe(success => {
        if (success) {
          if (this.groupId) {
            this._store.dispatch(new ClearGetEventsByPeriod());
          } else {
            this._store.dispatch(new ClearEvents());
          }
          this.onCancel();
        }
      });

    this._store
      .pipe(select(selectChangesEventFailure), takeUntil(this.destroy$))
      .subscribe(errors => {
        this.errors$ = errors;
        this.setControlErrors(errors);
      });

    if (!this.groupId) {
      this._store.dispatch(new GetActiveCoursesRequest());

      this._store
        .pipe(select(selectCoursesList), takeUntil(this.destroy$))
        .subscribe(courses => {
          this.courses$ = courses ? courses : [];
        });

      this._store
        .pipe(select(selectCourseGroups), takeUntil(this.destroy$))
        .subscribe(groups => {
          if (groups) {
            this.groups$ = groups.results;
            this.eventForm.controls['group'].enable();
          }
        });

      this.eventForm.controls['course'].valueChanges.subscribe(value => {
        this._store.dispatch(new GetActiveCourseGroupsRequest(value));
      });
    }
  }

  onSubmit(): void {
    let groupId;
    const formData = this.eventForm.value;
    let date = formData.date;

    if (date && date._i) {
      date = formatDatePicker(date._i);
    }

    if (this.groupId) {
      groupId = this.groupId;
    } else {
      groupId = this.eventForm.controls['group'].value;
    }

    if (isControlsIsValid(this.eventForm.controls)) {
      const eventData = {
        ...formData,
        group_id: groupId,
        date,
      };

      this._store.dispatch(new CreateEventRequest(eventData));
    }
  }

  onCancel() {
    this._dialogRef.close();
  }

  isShowNameField(): boolean {
    const index = Object.values(this.types).indexOf(this.types.OTH);
    return (
      this.eventForm.controls['type'].value === Object.keys(this.types)[index]
    );
  }

  get course() {
    return this.eventForm.get('course');
  }

  get group() {
    return this.eventForm.get('group');
  }

  get type() {
    return this.eventForm.get('type');
  }

  get theme() {
    return this.eventForm.get('theme');
  }

  get date() {
    return this.eventForm.get('date');
  }

  get name() {
    return this.eventForm.get('name');
  }

  get time() {
    return this.eventForm.get('time');
  }

  getMessageFormError(name: string): string | null {
    if (this.errors$ && this.errors$[name]) {
      return this.errors$[name][0];
    }

    return null;
  }

  setControlErrors(errors: IFormErrors | null): void {
    if (errors) {
      Object.keys(errors).forEach(key => {
        if (this.eventForm.controls[key]) {
          this.eventForm.controls[key].setErrors({ errors: errors[key] });
        }
      });
    }
  }

  changeTypeHandler(e: MatSelectChange) {
    if (e.value === Object.keys(this.types)[1]) {
      this.eventForm.patchValue({ time: '11:00' });
    } else {
      this.eventForm.patchValue({ time: '19:00' });
    }
  }

  ngOnDestroy(): void {
    if (!this.groupId) {
      this._store.dispatch(new ClearCourseGroups());
    }

    this._store.dispatch(new ClearCreateEventErrors());
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
